import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight, FaTimes, FaUndo, FaSearchPlus, FaSearchMinus, FaBars, FaSave, FaQuestionCircle } from 'react-icons/fa';
import { auth } from '../../firebase';

/**
 * violations를 그룹으로 묶는 함수
 * 조건: shape_path 동일 + para_idx 동일 + color/font 동일 + run_idx 연속
 */
const groupViolations = (violations, valueField) => {
  if (!violations || violations.length === 0) return [];

  // 원본 인덱스를 포함한 violations 배열 생성
  const violationsWithIndex = violations.map((v, idx) => ({ ...v, originalIndex: idx }));

  const groups = [];
  let currentGroup = null;

  violationsWithIndex.forEach((violation) => {
    // type이 'text'가 아니면 단독 그룹
    if (violation.type !== 'text' && violation.type !== 'table') {
      if (currentGroup) {
        groups.push(currentGroup);
        currentGroup = null;
      }
      groups.push({
        members: [violation],
        type: violation.type,
        value: violation[valueField],
        text: violation.text || '',
        firstMember: violation
      });
      return;
    }

    // text 타입인 경우 그룹핑 조건 확인
    const shapePathKey = JSON.stringify(violation.shape_path);
    const cellLocationKey = JSON.stringify(violation.cell_location);
    const paraIdx = violation.para_idx;
    const runIdx = violation.run_idx;
    const value = violation[valueField];

    if (currentGroup) {
      const lastMember = currentGroup.members[currentGroup.members.length - 1];
      const lastShapePathKey = JSON.stringify(lastMember.shape_path);
      const lastCellLocationKey = JSON.stringify(lastMember.cell_location);
      const lastParaIdx = lastMember.para_idx;
      const lastRunIdx = lastMember.run_idx;
      const lastValue = lastMember[valueField];

      // 같은 그룹 조건: shape_path 동일 + para_idx 동일 + color/font 동일 + run_idx 연속
      const isSameShapePath = shapePathKey === lastShapePathKey;
      const isSameCellLocation = cellLocationKey === lastCellLocationKey;
      const isSameParaIdx = paraIdx === lastParaIdx;
      const isSameValue = value?.toUpperCase() === lastValue?.toUpperCase();
      const isConsecutiveRun = runIdx === lastRunIdx + 1;

      if (isSameShapePath && isSameParaIdx && isSameValue && isConsecutiveRun && isSameCellLocation) {
        // 같은 그룹에 추가
        currentGroup.members.push(violation);
        currentGroup.text += violation.text || '';
      } else {
        // 새 그룹 시작
        groups.push(currentGroup);
        currentGroup = {
          members: [violation],
          type: violation.type,
          value: value,
          text: violation.text || '',
          firstMember: violation
        };
      }
    } else {
      // 첫 그룹 시작
      currentGroup = {
        members: [violation],
        type: violation.type,
        value: value,
        text: violation.text || '',
        firstMember: violation
      };
    }
  });

  // 마지막 그룹 추가
  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
};

const ResultModal = ({
  modalType, // 'color' | 'font'
  isOpen,
  onClose,
  slides,
  modalSlideIndex,
  getCurrentSlideResult,
  goToPrevModalSlide,
  goToNextModalSlide,
  violationChanges, // 현재 슬라이드 변경사항만
  allViolationChanges, // 전체 슬라이드 변경사항
  handleViolationChange,
  handleResetViolation,
  handlePPTModification,
  slideSize,
  isModifying,
  isMobile = false,
  // 브랜드킷 관련 props
  brandKitColors = [],
  brandKitFonts = [],
  totalViolations = 0, // 메타데이터에서 가져온 전체 위반 개수
  onSaveDraft, // 임시저장 함수
  initialState = {} // 초기 상태 (모달 열 때의 상태)
}) => {
  // 선택된 카드 인덱스들
  const [selectedCards, setSelectedCards] = useState([]);
  // 공통 입력값 (색상 HEX 또는 폰트명)
  const [commonInputValue, setCommonInputValue] = useState('');
  // 브랜드킷 드롭다운 열림 상태
  const [isBrandKitDropdownOpen, setIsBrandKitDropdownOpen] = useState(false);
  // 드롭다운 ref (외부 클릭 감지용)
  const brandKitDropdownRef = useRef(null);
  // 이미지 로드 상태
  const [imageLoaded, setImageLoaded] = useState(false);
  // 노란색 하이라이트 (클릭 시 깜빡임)
  const [yellowHighlight, setYellowHighlight] = useState(null);
  // 노란 하이라이트 타이머 ref
  const yellowHighlightTimerRef = useRef(null);
  // 리사이즈 감지용 (useMemo 재계산 트리거)
  const [resizeKey, setResizeKey] = useState(0);
  // 줌 관련 상태
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const zoomContainerRef = useRef(null);
  const imageRef = useRef(null);
  // 모바일 슬라이드바 상태 (기본값: 열림)
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  // 온보딩 상태
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  const { t } = useTranslation();

  // 초기 상태와 비교하여 변경사항이 있는지 계산
  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(allViolationChanges || {}) !== JSON.stringify(initialState || {});
  }, [allViolationChanges, initialState]);

  // 온보딩 단계 정의
  const onboardingSteps = [
    {
      target: 'violation-list',
      title: t('onboarding.colorFont.step1Title'),
      description: t('onboarding.colorFont.step1Desc'),
      position: 'right'
    },
    {
      target: 'violation-card',
      title: t('onboarding.colorFont.step2Title'),
      description: t('onboarding.colorFont.step2Desc'),
      position: 'right'
    },
    {
      target: 'input-area',
      title: t('onboarding.colorFont.step3Title'),
      description: t('onboarding.colorFont.step3Desc'),
      position: 'right'
    },
    {
      target: 'slide-preview',
      title: t('onboarding.colorFont.step4Title'),
      description: t('onboarding.colorFont.step4Desc'),
      position: 'left'
    },
    {
      target: 'cf-zoom-controls',
      title: t('onboarding.colorFont.step5Title'),
      description: t('onboarding.colorFont.step5Desc'),
      position: 'center'
    },
    {
      target: 'cf-footer-buttons',
      title: t('onboarding.colorFont.step6Title'),
      description: t('onboarding.colorFont.step6Desc'),
      position: 'top'
    }
  ];

  // 모달이 열릴 때 온보딩 자동 시작 (처음 사용자만)
  useEffect(() => {
    if (isOpen) {
      const userId = auth.currentUser?.uid;
      if (userId) {
        const completed = localStorage.getItem(`onboarding_colorFont_completed_${userId}`);
        if (!completed) {
          setShowOnboarding(true);
          setOnboardingStep(0);
        }
      }
    }
  }, [isOpen]);

  const handleNextStep = () => {
    if (onboardingStep < onboardingSteps.length - 1) {
      setOnboardingStep(prev => prev + 1);
    } else {
      setShowOnboarding(false);
      setOnboardingStep(0);
      const userId = auth.currentUser?.uid;
      if (userId) {
        localStorage.setItem(`onboarding_colorFont_completed_${userId}`, 'true');
      }
    }
  };

  const handlePrevStep = () => {
    if (onboardingStep > 0) {
      setOnboardingStep(prev => prev - 1);
    }
  };

  const handleSkipOnboarding = () => {
    setShowOnboarding(false);
    setOnboardingStep(0);
    const userId = auth.currentUser?.uid;
    if (userId) {
      localStorage.setItem(`onboarding_colorFont_completed_${userId}`, 'true');
    }
  };

  // 줌 관련 상수
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 2;
  const ZOOM_STEP = 0.25;

  const isColorType = modalType === 'color';
  const valueField = isColorType ? 'color' : 'font';

  const currentResult = isOpen ? getCurrentSlideResult(modalSlideIndex) : null;

  // violations를 useMemo로 감싸서 매 렌더링마다 새 배열 생성 방지
  const violations = useMemo(() => {
    return currentResult?.violations || [];
  }, [currentResult]);

  // violations를 그룹으로 변환 (useMemo로 최적화)
  const violationGroups = useMemo(() => {
    return groupViolations(violations, valueField);
  }, [violations, valueField]);

  // 슬라이드 변경 시 선택 및 줌 초기화
  useEffect(() => {
    setSelectedCards([]);
    setCommonInputValue('');
    setImageLoaded(false);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setIsBrandKitDropdownOpen(false);
  }, [modalSlideIndex]);

  // 브랜드킷 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandKitDropdownRef.current && !brandKitDropdownRef.current.contains(event.target)) {
        setIsBrandKitDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 모달 닫힐 때 줌 초기화
  useEffect(() => {
    if (!isOpen) {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // 리사이즈 감지
  useEffect(() => {
    const handleResize = () => setResizeKey(n => n + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 빨간 박스 좌표 계산 (선택된 카드 + 수정된 카드) - 이미지 내부 좌표
  const selectedHighlights = useMemo(() => {
    // resizeKey를 의존성에 포함하여 리사이즈 시 재계산
    void resizeKey;

    if (!isOpen || !imageLoaded) return [];

    const imageElement = imageRef.current;
    if (!imageElement || !slideSize?.width || !slideSize?.height) return [];

    // 수정된 그룹 인덱스 계산
    const changedGroupIndices = violationGroups
      .map((group, idx) => ({ group, idx }))
      .filter(({ group }) => {
        const originalIndex = group.firstMember.originalIndex;
        if (!violationChanges?.hasOwnProperty(originalIndex)) return false;

        const originalValue = group.value;
        const newValue = violationChanges[originalIndex];
        const normalizedNew = isColorType ? newValue?.toUpperCase() : newValue;
        const normalizedOriginal = isColorType ? originalValue?.toUpperCase() : originalValue;

        return normalizedNew !== normalizedOriginal;
      })
      .map(({ idx }) => idx);

    // 선택 + 수정된 카드 합치기 (중복 제거)
    const allIndices = [...new Set([...selectedCards, ...changedGroupIndices])];
    if (allIndices.length === 0) return [];

    // 이미지 내부 좌표 계산 (position: absolute용)
    const displayWidth = imageElement.clientWidth;
    const displayHeight = imageElement.clientHeight;
    const xRatio = displayWidth / slideSize.width;
    const yRatio = displayHeight / slideSize.height;

    return allIndices.map(groupIndex => {
      const group = violationGroups[groupIndex];
      if (!group?.firstMember?.offset || !group?.firstMember?.size) return null;

      return {
        groupIndex,
        x: group.firstMember.offset[0] * xRatio,
        y: group.firstMember.offset[1] * yRatio,
        width: group.firstMember.size[0] * xRatio,
        height: group.firstMember.size[1] * yRatio
      };
    }).filter(Boolean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, imageLoaded, slideSize, violationGroups, selectedCards, violationChanges, isColorType, resizeKey]);

  // 노란색 하이라이트 표시 함수 (그룹의 첫 번째 run만) - 이미지 내부 좌표
  const showYellowHighlight = (groupIndex) => {
    if (!isOpen) return;
    const imageElement = imageRef.current;
    if (!imageElement || !slideSize?.width || !slideSize?.height) return;

    const group = violationGroups[groupIndex];
    if (!group?.firstMember?.offset || !group?.firstMember?.size) return;

    const displayWidth = imageElement.clientWidth;
    const displayHeight = imageElement.clientHeight;
    const xRatio = displayWidth / slideSize.width;
    const yRatio = displayHeight / slideSize.height;

    setYellowHighlight({
      x: group.firstMember.offset[0] * xRatio,
      y: group.firstMember.offset[1] * yRatio,
      width: group.firstMember.size[0] * xRatio,
      height: group.firstMember.size[1] * yRatio
    });

    // 이전 타이머가 있으면 취소
    if (yellowHighlightTimerRef.current) {
      clearTimeout(yellowHighlightTimerRef.current);
    }

    // 1.5초 후 노란색 하이라이트 제거
    yellowHighlightTimerRef.current = setTimeout(() => {
      setYellowHighlight(null);
      yellowHighlightTimerRef.current = null;
    }, 1500);
  };

  // 줌 인/아웃 핸들러 (버튼용 - 중앙 기준)
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM);
    setZoomLevel(newZoom);
    if (newZoom === MIN_ZOOM) {
      setPanOffset({ x: 0, y: 0 });
    }
  };

  // 마우스 휠 줌 핸들러 (마우스 위치 기준)
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    const container = zoomContainerRef.current;
    const image = imageRef.current;
    if (!container || !image) return;

    const containerRect = container.getBoundingClientRect();

    // 컨테이너 내에서 마우스 위치
    const mouseX = e.clientX - containerRect.left;
    const mouseY = e.clientY - containerRect.top;

    const zoomDelta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel + zoomDelta));

    if (newZoom !== zoomLevel) {
      // 현재 이미지에서 마우스가 가리키는 지점 (이미지 좌표계)
      const imageX = (mouseX - panOffset.x) / zoomLevel;
      const imageY = (mouseY - panOffset.y) / zoomLevel;

      // 새 줌에서 같은 이미지 지점이 마우스 아래에 오도록 pan 조정
      const newPanX = mouseX - imageX * newZoom;
      const newPanY = mouseY - imageY * newZoom;

      setZoomLevel(newZoom);
      if (newZoom === MIN_ZOOM) {
        setPanOffset({ x: 0, y: 0 });
      } else {
        setPanOffset({ x: newPanX, y: newPanY });
      }
    }
  }, [zoomLevel, panOffset]);

  // 드래그 시작 (마우스)
  const handleMouseDown = (e) => {
    if (zoomLevel > MIN_ZOOM) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    }
  };

  // 터치 드래그 시작 (모바일)
  const handleTouchStart = (e) => {
    if (zoomLevel > MIN_ZOOM && e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y });
    }
  };

  // 드래그 중 (이미지의 30%가 항상 보이도록 제한)
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const image = imageRef.current;
    if (!image) {
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
      return;
    }

    // 줌 적용 후 이미지 크기
    const scaledWidth = image.clientWidth * zoomLevel;
    const scaledHeight = image.clientHeight * zoomLevel;

    // 이미지의 30%가 항상 보이도록 드래그 범위 제한
    const minVisible = 0.3;
    const limitX = scaledWidth * minVisible;
    const limitY = scaledHeight * minVisible;

    // clamp: 범위 내로 제한
    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
    const newX = clamp(e.clientX - dragStart.x, limitX - scaledWidth, limitX);
    const newY = clamp(e.clientY - dragStart.y, limitY - scaledHeight, limitY);

    setPanOffset({ x: newX, y: newY });
  }, [isDragging, dragStart, zoomLevel]);

  // 드래그 종료
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 터치 이동 (모바일)
  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const image = imageRef.current;

    if (!image) {
      setPanOffset({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
      return;
    }

    const scaledWidth = image.clientWidth * zoomLevel;
    const scaledHeight = image.clientHeight * zoomLevel;

    const minVisible = 0.3;
    const limitX = scaledWidth * minVisible;
    const limitY = scaledHeight * minVisible;

    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
    const newX = clamp(touch.clientX - dragStart.x, limitX - scaledWidth, limitX);
    const newY = clamp(touch.clientY - dragStart.y, limitY - scaledHeight, limitY);

    setPanOffset({ x: newX, y: newY });
  }, [isDragging, dragStart, zoomLevel]);

  // 터치 종료 (모바일)
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 마우스/터치 이벤트 리스너 등록
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // 휠 이벤트 리스너 등록 (passive: false로 preventDefault 허용)
  useEffect(() => {
    const container = zoomContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, isOpen, imageLoaded]);

  if (!isOpen) return null;

  // 타입별 설정
  const config = {
    color: {
      title: t('resultModal.color.title'),
      violationsText: t('resultModal.color.violationsText'),
      complianceText: t('resultModal.color.complianceText'),
      valueField: 'color',
      inputPlaceholder: t('resultModal.color.inputPlaceholder'),
      inputLabel: t('resultModal.color.inputLabel'),
      selectLabel: t('resultModal.color.selectLabel'),
      noViolation: t('resultModal.color.noViolation')
    },
    font: {
      title: t('resultModal.font.title'),
      violationsText: t('resultModal.font.violationsText'),
      complianceText: t('resultModal.font.complianceText'),
      valueField: 'font',
      inputPlaceholder: t('resultModal.font.inputPlaceholder'),
      inputLabel: t('resultModal.font.inputLabel'),
      selectLabel: t('resultModal.font.selectLabel'),
      noViolation: t('resultModal.font.noViolation')
    }
  };

  const currentConfig = config[modalType] || config.color;

  // 현재 슬라이드의 모든 그룹이 선택되었는지 여부
  const isAllCardsSelected = violationGroups.length > 0 && selectedCards.length === violationGroups.length;

  // 전체 선택 / 전체 해제 토글 (현재 슬라이드 기준)
  const handleToggleSelectAll = () => {
    if (isAllCardsSelected) {
      setSelectedCards([]);
    } else {
      setSelectedCards(violationGroups.map((_, idx) => idx));
    }
  };

  // 카드(그룹) 선택/해제 토글
  const toggleCardSelection = (groupIndex) => {
    const isCurrentlySelected = selectedCards.includes(groupIndex);

    // 선택할 때만 노란색 하이라이트 표시
    if (!isCurrentlySelected) {
      showYellowHighlight(groupIndex);
    }

    setSelectedCards(prev => {
      if (prev.includes(groupIndex)) {
        return prev.filter(i => i !== groupIndex);
      } else {
        return [...prev, groupIndex];
      }
    });
  };

  // 전체 되돌리기 (현재 슬라이드의 모든 그룹의 모든 run)
  const handleResetAll = () => {
    violationGroups.forEach(group => {
      group.members.forEach(member => {
        handleResetViolation(member.originalIndex);
      });
    });
  };

  // 선택된 그룹들만 되돌리기 (그룹 내 모든 run)
  const handleResetSelected = () => {
    selectedCards.forEach(groupIndex => {
      const group = violationGroups[groupIndex];
      if (group) {
        group.members.forEach(member => {
          handleResetViolation(member.originalIndex);
        });
      }
    });
    setSelectedCards([]);
  };

  // 선택된 그룹들에 공통 값 적용 (그룹 내 모든 run에 적용)
  const applyToSelected = () => {
    if (selectedCards.length === 0 || !commonInputValue.trim()) return;

    selectedCards.forEach(groupIndex => {
      const group = violationGroups[groupIndex];
      if (group) {
        // 그룹 내 모든 멤버(run)에 값 적용
        group.members.forEach(member => {
          handleViolationChange(member.originalIndex, commonInputValue);
        });
      }
    });
    setSelectedCards([]);
  };

  // 타입별로 그룹 분류
  const groupsByType = violationGroups.reduce((acc, group, groupIndex) => {
    const type = group.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push({ ...group, groupIndex });
    return acc;
  }, {});

  const typeLabels = {
    'table': t('resultModal.typeTable'),
    'shape': t('resultModal.typeShape'),
    'text': t('resultModal.typeText')
  };

  // 원본과 다른 값만 필터링
  const getActualChanges = () => {
    if (!violationChanges) return {};

    const actualChanges = {};
    Object.entries(violationChanges).forEach(([index, newValue]) => {
      const violation = violations[parseInt(index)];
      if (!violation) return;

      const originalValue = violation[currentConfig.valueField];
      const normalizedNew = isColorType ? newValue.toUpperCase() : newValue;
      const normalizedOriginal = isColorType ? originalValue.toUpperCase() : originalValue;

      if (normalizedNew !== normalizedOriginal) {
        actualChanges[index] = newValue;
      }
    });

    return actualChanges;
  };

  const actualChanges = getActualChanges();

  // 전체 슬라이드 변경사항 계산
  const getAllActualChanges = () => {
    if (!allViolationChanges) return {};

    const result = {};

    Object.entries(allViolationChanges).forEach(([slideIndex, slideChanges]) => {
      const slideResult = getCurrentSlideResult(parseInt(slideIndex));
      if (!slideResult || !slideResult.violations) return;

      const actualSlideChanges = {};
      Object.entries(slideChanges).forEach(([violationIndex, newValue]) => {
        const violation = slideResult.violations[parseInt(violationIndex)];
        if (!violation) return;

        if (isColorType) {
          const hexValue = newValue.replace('#', '');
          if (!/^[0-9A-Fa-f]{6}$/.test(hexValue)) {
            return;
          }
        }

        const originalValue = violation[currentConfig.valueField];
        const normalizedNew = isColorType ? newValue.toUpperCase() : newValue;
        const normalizedOriginal = isColorType ? originalValue.toUpperCase() : originalValue;

        if (normalizedNew !== normalizedOriginal) {
          actualSlideChanges[violationIndex] = newValue;
        }
      });

      if (Object.keys(actualSlideChanges).length > 0) {
        result[slideIndex] = actualSlideChanges;
      }
    });

    return result;
  };

  const allActualChanges = getAllActualChanges();

  // 변경된 run 개수 (백엔드 전송용)
  const getTotalChangeCount = () => {
    return Object.values(allActualChanges).reduce((total, slideChanges) => {
      return total + Object.keys(slideChanges).length;
    }, 0);
  };

  // 변경된 그룹 개수 (UI 표시용)
  const getTotalChangedGroupsCount = () => {
    let count = 0;
    Object.entries(allActualChanges).forEach(([slideIndex, slideChanges]) => {
      const slideResult = getCurrentSlideResult(parseInt(slideIndex));
      if (!slideResult?.violations) return;

      const slideGroups = groupViolations(slideResult.violations, valueField);
      slideGroups.forEach(group => {
        // 그룹 내 첫 번째 멤버가 변경되었으면 그룹이 변경된 것
        if (slideChanges.hasOwnProperty(group.firstMember.originalIndex)) {
          count++;
        }
      });
    });
    return count;
  };

  const getTotalSlideCount = () => {
    return Object.keys(allActualChanges).length;
  };

  // 색상 유효성 검사
  const isValidColorInput = isColorType
    ? /^[0-9A-Fa-f]{6}$/.test(commonInputValue.replace('#', ''))
    : commonInputValue.trim().length > 0;

  // 그룹의 현재 값 가져오기 (첫 번째 멤버의 변경된 값 또는 원본)
  const getGroupCurrentValue = (group) => {
    const firstMember = group.firstMember;
    const originalIndex = firstMember.originalIndex;
    return violationChanges?.hasOwnProperty(originalIndex)
      ? violationChanges[originalIndex]
      : group.value;
  };

  // 그룹이 수정되었는지 확인 (그룹 선택 시 모든 멤버에 동일하게 적용되므로 첫 번째 멤버로 확인)
  const isGroupChanged = (group) => {
    return actualChanges.hasOwnProperty(group.firstMember.originalIndex);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      {/* CSS 애니메이션 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes blink {
            0% { opacity: 0.4; }
            100% { opacity: 0.8; }
          }
        `
      }} />

      <div id="color-font-result-modal" style={{
        background: '#ffffff',
        borderRadius: isMobile ? '12px' : '20px',
        width: isMobile ? '100vw' : '95vw',
        maxWidth: isMobile ? '100vw' : '1400px',
        height: isMobile ? '100vh' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
        position: 'relative'
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: '1px solid #e5e7eb',
          background: '#ffffff'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '12px'
          }}>
            {/* 모바일 햄버거 메뉴 */}
            {isMobile && (
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                style={{
                  background: showMobileSidebar ? '#1A62EA' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: showMobileSidebar ? '#ffffff' : '#6b7280',
                  transition: 'all 0.2s ease'
                }}
              >
                <FaBars size={18} />
              </button>
            )}
            <h2 style={{
              fontSize: isMobile ? '14px' : '18px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>
              {currentConfig.title}
            </h2>
            <span style={{
              background: totalViolations > 0 ? '#fee2e2' : '#d1fae5',
              color: totalViolations > 0 ? '#dc2626' : '#059669',
              padding: isMobile ? '3px 8px' : '4px 10px',
              borderRadius: '12px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600'
            }}>
              {t('resultModal.totalViolations', { count: totalViolations })}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* 도움말 버튼 */}
            <button
              onClick={() => setShowOnboarding(true)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF4FE'; e.currentTarget.style.color = '#1A62EA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              title={t('onboarding.helpButton')}
            >
              <FaQuestionCircle size={isMobile ? 18 : 20} />
            </button>
            {/* 닫기 버튼 */}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                transition: 'all 0.2s ease'
              }}
            >
              <FaTimes size={isMobile ? 18 : 20} />
            </button>
          </div>
        </div>

        {/* 메인 콘텐츠 */}
        <div style={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* 왼쪽: 위반 항목 목록 (모바일에서는 슬라이드 오버레이) */}
          <div style={{
            width: isMobile ? '85%' : '420px',
            flexShrink: 0,
            maxWidth: isMobile ? '320px' : '420px',
            borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            // 모바일 슬라이드바 스타일
            ...(isMobile && {
              position: 'absolute',
              top: 0,
              left: showMobileSidebar ? 0 : '-100%',
              height: '100%',
              zIndex: 100,
              boxShadow: showMobileSidebar ? '4px 0 20px rgba(0, 0, 0, 0.15)' : 'none',
              transition: 'left 0.3s ease'
            })
          }}>
            {/* 목록 헤더 */}
            <div id="violation-list" style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#EDF4FE',
              position: 'relative'
            }}>
              {/* 모바일 슬라이드바 닫기 버튼 */}
              {isMobile && (
                <button
                  onClick={() => setShowMobileSidebar(false)}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280'
                  }}
                >
                  <FaTimes size={16} />
                </button>
              )}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '6px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0
                }}>
                  {t('resultModal.slideResult', { num: modalSlideIndex + 1, type: isColorType ? t('resultModal.color.noViolation') : t('resultModal.font.noViolation') })}
                </h3>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px'
              }}>
                <span style={{
                  background: currentResult?.has_violations ? '#fee2e2' : '#d1fae5',
                  color: currentResult?.has_violations ? '#dc2626' : '#059669',
                  padding: '3px 8px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {currentResult?.has_violations
                    ? `${violationGroups.length}${currentConfig.violationsText}`
                    : currentConfig.complianceText
                  }
                </span>
                {getTotalChangedGroupsCount() > 0 && (
                  <span style={{
                    background: '#dbeafe',
                    color: '#1d4ed8',
                    padding: '3px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {t('resultModal.modifiedCount', { count: getTotalChangedGroupsCount() })}
                  </span>
                )}
              </div>
            </div>

            {/* 위반 항목 목록 */}
            <div id="violation-card" style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 20px',
              background: '#EDF4FE'
            }}>
              {violationGroups.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#9ca3af'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>
                    {t('resultModal.noViolationInSlide', { type: isColorType ? t('resultModal.color.noViolation') : t('resultModal.font.noViolation') })}
                  </div>
                </div>
              ) : (
                Object.entries(groupsByType).map(([type, typeGroups]) => (
                  <div key={type} style={{ marginBottom: '16px' }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      {typeLabels[type]} {t('resultModal.itemCount', { count: typeGroups.length })}
                    </div>

                    {typeGroups.map((group) => {
                      const groupIndex = group.groupIndex;
                      const isSelected = selectedCards.includes(groupIndex);
                      const currentValue = getGroupCurrentValue(group);
                      const isChanged = isGroupChanged(group);

                      return (
                        <div
                          key={groupIndex}
                          onClick={() => toggleCardSelection(groupIndex)}
                          style={{
                            background: isSelected ? '#2563EB' : '#ffffff',
                            border: isSelected ? '2px solid #1d4ed8' : '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {/* 색상/폰트 + 텍스트 */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            {isColorType && (
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: `#${currentValue}`,
                                border: '1px solid #e5e7eb',
                                flexShrink: 0
                              }}></div>
                            )}

                            <div style={{ flex: 1, minWidth: 0, paddingLeft: '8px' }}>
                              <div style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: isSelected ? '#ffffff' : '#374151',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {isColorType ? `#${currentValue}` : currentValue}
                              </div>
                              {group.text && (
                                <div style={{
                                  fontSize: '14px',
                                  color: isSelected ? '#e5e7eb' : '#6b7280',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  "{group.text}"
                                </div>
                              )}
                            </div>

                            {isChanged && (
                              <span style={{
                                background: isSelected ? 'rgba(219, 234, 254, 0.9)' : '#dbeafe',
                                color: '#1d4ed8',
                                padding: '2px 8px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                flexShrink: 0
                              }}>
                                {t('resultModal.modified')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* 하단 입력 영역 */}
            {violationGroups.length > 0 && (
              <div id="input-area" style={{
                padding: '16px 20px',
                borderTop: '1px solid #e5e7eb',
                background: '#ffffff'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    {isColorType ? t('resultModal.colorSelect') : t('resultModal.fontSelect')}
                  </span>
                  <button
                    onClick={handleToggleSelectAll}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      background: isAllCardsSelected ? '#fee2e2' : '#f0fdf4',
                      color: isAllCardsSelected ? '#dc2626' : '#166534',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isAllCardsSelected ? t('resultModal.deselectAll') : t('resultModal.selectAll')}
                  </button>
                </div>

                {/* 브랜드킷 커스텀 드롭다운 */}
                <div
                  ref={brandKitDropdownRef}
                  style={{
                    position: 'relative',
                    marginBottom: '12px'
                  }}
                >
                  {/* 드롭다운 트리거 버튼 */}
                  <button
                    onClick={() => setIsBrandKitDropdownOpen(!isBrandKitDropdownOpen)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#ffffff',
                      fontSize: '14px',
                      color: '#374151',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ color: '#6b7280' }}>
                      {isColorType
                        ? t('resultModal.selectColorFromBrandkit')
                        : t('resultModal.selectFontFromBrandkit')
                      }
                    </span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      style={{
                        transform: isBrandKitDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path fill="#6b7280" d="M6 8L1 3h10z"/>
                    </svg>
                  </button>

                  {/* 드롭다운 메뉴 */}
                  {isBrandKitDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 200
                    }}>
                      {isColorType ? (
                        brandKitColors.length > 0 ? (
                          brandKitColors.map((color, idx) => {
                            const hexColor = color.replace('#', '').toUpperCase();
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  setCommonInputValue(hexColor);
                                  setIsBrandKitDropdownOpen(false);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: 'none',
                                  background: commonInputValue === hexColor ? '#EDF4FE' : '#ffffff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  textAlign: 'left',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (commonInputValue !== hexColor) {
                                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (commonInputValue !== hexColor) {
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                  }
                                }}
                              >
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  backgroundColor: `#${hexColor}`,
                                  border: '1px solid #e5e7eb',
                                  flexShrink: 0
                                }} />
                                <span style={{
                                  fontSize: '14px',
                                  fontWeight: commonInputValue === hexColor ? '600' : '400',
                                  color: commonInputValue === hexColor ? '#1A62EA' : '#374151'
                                }}>
                                  #{hexColor}
                                </span>
                              </button>
                            );
                          })
                        ) : (
                          <div style={{
                            padding: '16px 12px',
                            textAlign: 'center',
                            color: '#9ca3af',
                            fontSize: '13px'
                          }}>
                            {t('resultModal.noColorsInBrandkit')}
                          </div>
                        )
                      ) : (
                        brandKitFonts.length > 0 ? (
                          brandKitFonts.map((font, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setCommonInputValue(font);
                                setIsBrandKitDropdownOpen(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: 'none',
                                background: commonInputValue === font ? '#EDF4FE' : '#ffffff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                textAlign: 'left',
                                transition: 'background-color 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (commonInputValue !== font) {
                                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (commonInputValue !== font) {
                                  e.currentTarget.style.backgroundColor = '#ffffff';
                                }
                              }}
                            >
                              <span style={{
                                fontSize: '14px',
                                fontWeight: commonInputValue === font ? '600' : '400',
                                color: commonInputValue === font ? '#1A62EA' : '#374151',
                                fontFamily: font
                              }}>
                                {font}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div style={{
                            padding: '16px 12px',
                            textAlign: 'center',
                            color: '#9ca3af',
                            fontSize: '13px'
                          }}>
                            {t('resultModal.noFontsInBrandkit')}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                {/* 직접 입력 UI */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  {isColorType && (
                    <input
                      type="color"
                      value={commonInputValue ? `#${commonInputValue}` : '#FFFFFF'}
                      onChange={(e) => setCommonInputValue(e.target.value.slice(1).toUpperCase())}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    />
                  )}
                  <input
                    type="text"
                    value={isColorType ? `#${commonInputValue}` : commonInputValue}
                    onChange={(e) => {
                      const val = isColorType
                        ? e.target.value.replace('#', '').toUpperCase()
                        : e.target.value;
                      if (isColorType && !/^[0-9A-F]{0,6}$/.test(val)) return;
                      setCommonInputValue(val);
                    }}
                    placeholder={isColorType ? t('resultModal.directInputColor') : t('resultModal.directInputFont')}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: commonInputValue && !isValidColorInput
                        ? '1px solid #fca5a5'
                        : '1px solid #e5e7eb',
                      background: commonInputValue && !isValidColorInput
                        ? '#fef2f2'
                        : '#ffffff',
                      fontSize: '14px',
                      color: '#374151'
                    }}
                  />
                </div>

                {commonInputValue && !isValidColorInput && isColorType && (
                  <div style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    marginBottom: '8px'
                  }}>
                    {t('resultModal.hexValidation')}
                  </div>
                )}

                {/* 되돌리기 + 변경 적용 버튼 */}
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    onClick={handleResetSelected}
                    disabled={selectedCards.length === 0}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#ffffff',
                      color: selectedCards.length === 0 ? '#9ca3af' : '#374151',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: selectedCards.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <FaUndo size={12} />
                    {t('resultModal.reset')}
                  </button>
                  <button
                    onClick={applyToSelected}
                    disabled={selectedCards.length === 0 || !isValidColorInput}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: (selectedCards.length > 0 && isValidColorInput) ? '#1A62EA' : '#9ca3af',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: (selectedCards.length > 0 && isValidColorInput) ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {t('resultModal.applyChange')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 모바일 슬라이드바 배경 오버레이 */}
          {isMobile && showMobileSidebar && (
            <div
              onClick={() => setShowMobileSidebar(false)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.3)',
                zIndex: 99
              }}
            />
          )}

          {/* 오른쪽: 슬라이드 이미지 */}
          <div id="slide-preview" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            position: 'relative'
          }}>
            {/* 슬라이드 네비게이션 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              padding: '14px',
              borderBottom: '1px solid #e5e7eb',
              background: '#ffffff'
            }}>
              <button
                onClick={goToPrevModalSlide}
                disabled={modalSlideIndex === 0}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: modalSlideIndex === 0 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  color: modalSlideIndex === 0 ? '#d1d5db' : '#6b7280',
                  fontSize: '20px'
                }}
              >
                <FaChevronLeft />
              </button>

              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#374151'
              }}>
                {modalSlideIndex + 1} / {slides.length}
              </span>

              <button
                onClick={goToNextModalSlide}
                disabled={modalSlideIndex === slides.length - 1}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: modalSlideIndex === slides.length - 1 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  color: modalSlideIndex === slides.length - 1 ? '#d1d5db' : '#6b7280',
                  fontSize: '20px'
                }}
              >
                <FaChevronRight />
              </button>
            </div>

            {/* 슬라이드 이미지 - TypoResultModal과 동일한 구조 */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div
                ref={zoomContainerRef}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{
                  position: 'relative',
                  cursor: zoomLevel > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  userSelect: 'none',
                  touchAction: zoomLevel > MIN_ZOOM ? 'none' : 'auto'
                }}
              >
                <div style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                  transformOrigin: '0 0',
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                  position: 'relative'
                }}>
                  <img
                    ref={imageRef}
                    id={`slide-image-${modalSlideIndex}`}
                    src={slides[modalSlideIndex]?.thumbnail_url}
                    alt={t('resultModal.slideImageAlt', { num: modalSlideIndex + 1 })}
                    onLoad={() => setImageLoaded(true)}
                    draggable={false}
                    style={{
                      maxWidth: isMobile ? 'calc(100vw - 40px)' : 'calc(100% - 40px)',
                      maxHeight: isMobile ? 'calc(100vh - 220px)' : 'calc(90vh - 240px)',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      userSelect: 'none',
                      display: 'block'
                    }}
                  />
                  {/* 선택된 카드들의 빨간색 하이라이트 (이미지 내부) */}
                  {selectedHighlights.map((highlight, idx) => (
                    <div
                      key={`red-${idx}`}
                      style={{
                        position: 'absolute',
                        left: highlight.x,
                        top: highlight.y,
                        width: highlight.width,
                        height: highlight.height,
                        border: '3px solid #dc2626',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        boxShadow: '0 0 0 1px rgba(220, 38, 38, 0.3)'
                      }}
                    />
                  ))}
                  {/* 노란색 하이라이트 (클릭 시 깜빡임, 이미지 내부) */}
                  {yellowHighlight && (
                    <div
                      style={{
                        position: 'absolute',
                        left: yellowHighlight.x,
                        top: yellowHighlight.y,
                        width: yellowHighlight.width,
                        height: yellowHighlight.height,
                        backgroundColor: 'rgba(255, 255, 0, 0.6)',
                        border: '2px solid rgba(255, 200, 0, 0.9)',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        animation: 'blink 0.6s ease-in-out infinite alternate'
                      }}
                    />
                  )}
                </div>
              </div>

              {/* 줌 컨트롤 버튼 - TypoResultModal과 동일 */}
              <div id="cf-zoom-controls" style={{
                position: 'absolute',
                bottom: '30px',
                right: '30px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                <button
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= MAX_ZOOM}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    background: zoomLevel >= MAX_ZOOM ? '#f3f4f6' : '#ffffff',
                    cursor: zoomLevel >= MAX_ZOOM ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: zoomLevel >= MAX_ZOOM ? '#9ca3af' : '#374151',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FaSearchPlus size={16} />
                </button>
                <div style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#374151',
                  padding: '4px 0'
                }}>
                  {Math.round(zoomLevel * 100)}%
                </div>
                <button
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= MIN_ZOOM}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    background: zoomLevel <= MIN_ZOOM ? '#f3f4f6' : '#ffffff',
                    cursor: zoomLevel <= MIN_ZOOM ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: zoomLevel <= MIN_ZOOM ? '#9ca3af' : '#374151',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FaSearchMinus size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 푸터 - 버튼 영역 */}
        <div id="cf-footer-buttons" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          background: '#ffffff'
        }}>
          {/* 왼쪽: 전체 되돌리기 + 이 슬라이드만 적용 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={handleResetAll}
              disabled={Object.keys(actualChanges).length === 0}
              style={{
                padding: '12px 20px',
                borderRadius: '10px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: Object.keys(actualChanges).length === 0 ? '#9ca3af' : '#374151',
                fontSize: '14px',
                fontWeight: '600',
                cursor: Object.keys(actualChanges).length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              <FaUndo size={14} />
              {t('resultModal.resetAll')}
            </button>

            {/* 임시저장 버튼 */}
            {onSaveDraft && (
              <button
                onClick={() => onSaveDraft(allViolationChanges)}
                disabled={!hasUnsavedChanges}
                style={{
                  padding: '12px 20px',
                  borderRadius: '10px',
                  border: '1px solid #d1d5db',
                  background: hasUnsavedChanges ? '#EDF4FE' : '#ffffff',
                  color: !hasUnsavedChanges ? '#9ca3af' : '#1A62EA',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: !hasUnsavedChanges ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <FaSave size={14} />
                {t('draft.saveButton')}
              </button>
            )}

            {/* 이 슬라이드만 적용 버튼 - 주석처리 */}
            {/* <button
              onClick={() => handlePPTModification(modalSlideIndex, actualChanges, false)}
              disabled={!hasChanges || isModifying}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: 'none',
                background: (hasChanges && !isModifying) ? '#1A62EA' : '#9ca3af',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (hasChanges && !isModifying) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              {isModifying ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  수정 중...
                </>
              ) : (
                `이 슬라이드만 적용 (${Object.keys(actualChanges).length}개 항목)`
              )}
            </button> */}
          </div>

          {/* 오른쪽: 닫기 + 전체 슬라이드 적용 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('common.close')}
            </button>

            {getTotalChangeCount() > 0 && (
              <button
                onClick={() => handlePPTModification(null, allActualChanges, true)}
                disabled={isModifying}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: isModifying ? '#9ca3af' : '#059669',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isModifying ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                {isModifying ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {t('resultModal.modifying')}
                  </>
                ) : (
                  t('resultModal.applyAllSlides', { count: getTotalSlideCount() })
                )}
              </button>
            )}
          </div>
        </div>

        {/* 온보딩 오버레이 */}
        {showOnboarding && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3000
          }}>
            {/* 배경 오버레이 (클릭 감지용, 투명) */}
            <div
              onClick={handleSkipOnboarding}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'transparent',
                zIndex: 3001
              }}
            />

            {/* 하이라이트 영역 */}
            {(() => {
              const targetElement = document.getElementById(onboardingSteps[onboardingStep]?.target);
              if (!targetElement) return null;

              const rect = targetElement.getBoundingClientRect();
              const modalElement = document.getElementById('color-font-result-modal');
              const modalRect = modalElement?.getBoundingClientRect() || { left: 0, top: 0 };

              const relativeRect = {
                left: rect.left - modalRect.left,
                top: rect.top - modalRect.top,
                width: rect.width,
                height: rect.height
              };

              const step = onboardingSteps[onboardingStep];
              let tooltipStyle = {
                position: 'absolute',
                background: '#ffffff',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                zIndex: 3003,
                maxWidth: '320px',
                minWidth: '280px'
              };

              // 툴팁 위치 계산
              switch (step.position) {
                case 'right':
                  tooltipStyle.left = relativeRect.left + relativeRect.width + 16;
                  tooltipStyle.top = relativeRect.top + relativeRect.height / 2;
                  tooltipStyle.transform = 'translateY(-50%)';
                  break;
                case 'left':
                  tooltipStyle.right = `calc(100% - ${relativeRect.left - 16}px)`;
                  tooltipStyle.top = relativeRect.top + relativeRect.height / 2;
                  tooltipStyle.transform = 'translateY(-50%)';
                  break;
                case 'bottom':
                  tooltipStyle.left = relativeRect.left + relativeRect.width / 2;
                  tooltipStyle.top = relativeRect.top + relativeRect.height + 16;
                  tooltipStyle.transform = 'translateX(-50%)';
                  break;
                case 'top':
                  tooltipStyle.left = relativeRect.left + relativeRect.width / 2;
                  tooltipStyle.bottom = `calc(100% - ${relativeRect.top - 16}px)`;
                  tooltipStyle.transform = 'translateX(-50%)';
                  break;
                case 'center':
                  tooltipStyle.left = '50%';
                  tooltipStyle.top = '50%';
                  tooltipStyle.transform = 'translate(-50%, -50%)';
                  break;
                default:
                  tooltipStyle.left = relativeRect.left + relativeRect.width + 16;
                  tooltipStyle.top = relativeRect.top;
              }

              return (
                <>
                  {/* 하이라이트 박스 */}
                  <div style={{
                    position: 'absolute',
                    left: relativeRect.left - 4,
                    top: relativeRect.top - 4,
                    width: relativeRect.width + 8,
                    height: relativeRect.height + 8,
                    border: '3px solid #1A62EA',
                    borderRadius: '8px',
                    zIndex: 3002,
                    pointerEvents: 'none',
                    background: 'transparent',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)'
                  }} />

                  {/* 툴팁 */}
                  <div style={tooltipStyle}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1A62EA'
                    }}>
                      {step.title}
                    </h3>
                    <p style={{
                      margin: '0 0 16px 0',
                      fontSize: '14px',
                      color: '#4b5563',
                      lineHeight: '1.5'
                    }}>
                      {step.description}
                    </p>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <button
                        onClick={handleSkipOnboarding}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#9ca3af',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        {t('onboarding.skip')}
                      </button>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {onboardingStep > 0 && (
                          <button
                            onClick={handlePrevStep}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              background: '#ffffff',
                              color: '#374151',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            {t('onboarding.prev')}
                          </button>
                        )}
                        <button
                          onClick={handleNextStep}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#1A62EA',
                            color: '#ffffff',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          {onboardingStep === onboardingSteps.length - 1 ? t('onboarding.finish') : t('onboarding.next')}
                        </button>
                      </div>
                    </div>
                    {/* 진행 표시 */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '6px',
                      marginTop: '16px'
                    }}>
                      {onboardingSteps.map((_, idx) => (
                        <div
                          key={idx}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: idx === onboardingStep ? '#1A62EA' : '#d1d5db',
                            transition: 'background 0.2s ease'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultModal;
